import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

// Components
import { WpIcon } from 'Wpds/components/icon';
import ColorConstants from 'Wpds/colors/ColorConstants';
import { WpText } from 'Wpds/components/text';
import { WpButton } from 'Wpds/components/button';
import { WpContainer, WpContainerHeader, WpContainerBody } from 'Wpds/components/container';
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import LoadingShimmer from 'Shared/components/LoadingShimmer/LoadingShimmer';

// Types
import { SummaryResponse } from 'Shared/api/BotManagement.api';

// Hooks
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useToggle } from 'Shared/utils/hooks';

// Actions
import { addItem } from 'Shared/actions/todos';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';

// Styles
import './SuggestedActionItems.scss';

type Props = {
  meetingSummary: SummaryResponse;
  fetchingMeetingSummary?: boolean;
};

const SuggestedActionItems = ({ meetingSummary, fetchingMeetingSummary = false }: Props) => {
  const dispatch = useDispatch();
  const workspace = useWorkspace();
  const [suggestedActionItems, setSuggestedActionItems] = useState<string[]>([]);
  const [isSaving, toggleIsSaving] = useToggle(false);

  useEffect(() => {
    if (meetingSummary?.data.action_items) {
      setSuggestedActionItems([...meetingSummary.data.action_items]);
    }
  }, [meetingSummary?.data.action_items]);

  const handleRemoveSuggestedItem = (indexToRemove: number) => {
    setSuggestedActionItems(items => items.filter((_, index) => index !== indexToRemove));
  };

  const handleAddSuggestedActionItem = (index: number) => {
    toggleIsSaving();
    const itemUuid = uuidv4();
    dispatch(
      addItem({
        itemUuid,
        body: suggestedActionItems[index],
        workspaceId: workspace.id,
        position: 1,
      })
    ).then(() => {
      toggleIsSaving();
      WpToastManager.success('Suggested action item created');
    });
    dispatch(updateSelectedUuids(itemUuid, { isOneSelected: true }));
    handleRemoveSuggestedItem(index);
  };

  const baseClassName = 'SuggestedActionItems';

  const suggestedItemContent =
    suggestedActionItems.length > 0 ? (
      suggestedActionItems.map((item, index) => (
        <div key={`${item}-${index}`} className={`${baseClassName}__item`}>
          <WpText variant="bodyMd" tag="span">
            {item}
          </WpText>
          <div className={`${baseClassName}__item-actions`}>
            <WpButton
              variant="secondary"
              size="sm"
              onClick={() => handleAddSuggestedActionItem(index)}
              disabled={isSaving}
            >
              <WpIcon name="plus" />
              Add
            </WpButton>
            <WpButton variant="tertiary" size="sm" onClick={() => handleRemoveSuggestedItem(index)} disabled={isSaving}>
              <WpIcon name="times" />
            </WpButton>
          </div>
        </div>
      ))
    ) : (
      <div className={`${baseClassName}__no-items`}>
        <WpIcon name="info" />
        <span>No suggested action items available.</span>
      </div>
    );

  return (
    <WpContainer className={baseClassName}>
      <WpContainerHeader>
        <WpIcon name="magic" color={ColorConstants.purple[800]} /> PI Companion Suggested Action Items
      </WpContainerHeader>
      <WpContainerBody className={`${baseClassName}__body`}>
        {fetchingMeetingSummary ? (
          <>
            <div className={`${baseClassName}__loading-item`}>
              <LoadingShimmer shape="rectangle" height="16px" width="80%" />
              <LoadingShimmer shape="square" height="16px" width="10%" />
              <LoadingShimmer shape="square" height="16px" width="10%" />
            </div>
            <div className={`${baseClassName}__loading-item`}>
              <LoadingShimmer shape="rectangle" height="16px" width="80%" />
              <LoadingShimmer shape="square" height="16px" width="10%" />
              <LoadingShimmer shape="square" height="16px" width="10%" />
            </div>
            <div className={`${baseClassName}__loading-item`}>
              <LoadingShimmer shape="rectangle" height="16px" width="80%" />
              <LoadingShimmer shape="square" height="16px" width="10%" />
              <LoadingShimmer shape="square" height="16px" width="10%" />
            </div>
          </>
        ) : (
          suggestedItemContent
        )}
      </WpContainerBody>
    </WpContainer>
  );
};

export default SuggestedActionItems;
